import React, { useCallback, useEffect, useState } from 'react';
import { Table, Tag, Button, Segmented, Space, Tooltip, message } from 'antd';
import { DownloadOutlined, CheckOutlined, UndoOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from 'Utils/api';
import { useIsMobile } from 'Utils/useIsMobile';
import { downloadCsv } from 'Utils/downloadCsv';
import MobileCard from '../MobileList/MobileCard';
import { List, CountRow, EmptyState } from '../ProductMobileList/ProductMobileList.style';
import { Toolbar } from './GiveawayEntriesTab.style';

interface Entry {
  id: string;
  firstName: string;
  lastName: string;
  street: string;
  postalCode: string;
  city: string;
  countryCode: string;
  phone: string;
  email: string;
  status: 'PENDING' | 'CONFIRMED';
  createdAt: string;
  confirmedAt: string | null;
  duplicateOf: { id: string; name: string; email: string } | null;
  duplicateDismissed: boolean;
}

type TFilter = 'all' | 'CONFIRMED' | 'PENDING' | 'duplicates';

const FILTERS: { value: TFilter; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'CONFIRMED', label: 'Bestätigt' },
  { value: 'PENDING', label: 'Unbestätigt' },
  { value: 'duplicates', label: 'Dubletten' },
];

const formatDate = (value: string | null) => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '–');

const GiveawayEntriesTab: React.FC<{ giveawayId: string }> = ({ giveawayId }) => {
  const isMobile = useIsMobile();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filter, setFilter] = useState<TFilter>('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'duplicates' ? { duplicates: 'true' } : filter === 'all' ? {} : { status: filter };
      const res = await apiClient.get(`/api/admin/giveaways/${giveawayId}/entries`, { params });
      setEntries(res.data.entries || []);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Laden der Teilnahmen');
    } finally {
      setLoading(false);
    }
  }, [giveawayId, filter]);

  useEffect(() => { load(); }, [load]);

  const setDismissed = async (entry: Entry, dismissed: boolean) => {
    const res = await apiClient.patch(`/api/admin/giveaways/${giveawayId}/entries/${entry.id}`, { duplicateDismissed: dismissed });
    if (res.data.status !== 'success') {
      message.error(res.data.message || 'Speichern fehlgeschlagen');
      return;
    }
    message.success(dismissed ? 'Teilnahme zählt wieder mit' : 'Wieder als Dublette markiert');
    load();
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get(`/api/admin/giveaways/${giveawayId}/entries/export`);
      if (!res.data?.csv) {
        message.error(res.data?.message || 'Export fehlgeschlagen');
        return;
      }
      downloadCsv(res.data.csv, res.data.filename);
      message.success('Teilnehmerliste heruntergeladen');
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  const statusTags = (entry: Entry) => (
    <Space size={4} wrap>
      {entry.status === 'CONFIRMED' ? <Tag color="green">Bestätigt</Tag> : <Tag color="orange">Unbestätigt</Tag>}
      {entry.duplicateOf && (
        <Tooltip title={`Gleiche Person wie ${entry.duplicateOf.name} (${entry.duplicateOf.email})`}>
          <Tag color={entry.duplicateDismissed ? 'default' : 'red'}>{entry.duplicateDismissed ? 'Keine Dublette' : 'Mögliche Dublette'}</Tag>
        </Tooltip>
      )}
    </Space>
  );

  const duplicateAction = (entry: Entry) => {
    if (!entry.duplicateOf) return null;
    return entry.duplicateDismissed
      ? { key: 'restore', label: 'Wieder als Dublette markieren', icon: <UndoOutlined />, onClick: () => setDismissed(entry, false) }
      : { key: 'dismiss', label: 'Ist keine Dublette', icon: <CheckOutlined />, onClick: () => setDismissed(entry, true) };
  };

  const toolbar = (
    <Toolbar>
      <Segmented<TFilter> value={filter} onChange={(v) => setFilter(v as TFilter)} options={FILTERS} data-testid="giveaway-entries-filter" />
      <Button icon={<DownloadOutlined />} onClick={exportCsv} loading={exporting} data-testid="giveaway-entries-export">
        Export (bestätigt, ohne Dubletten)
      </Button>
    </Toolbar>
  );

  if (isMobile) {
    return (
      <div>
        {toolbar}
        <CountRow><span>{entries.length} {entries.length === 1 ? 'Teilnahme' : 'Teilnahmen'}</span></CountRow>
        <List>
          {entries.map((entry) => {
            const action = duplicateAction(entry);
            return (
              <MobileCard
                key={entry.id}
                testId={`mobile-entry-card-${entry.id}`}
                overline={formatDate(entry.createdAt)}
                title={`${entry.firstName} ${entry.lastName}`}
                highlight={statusTags(entry)}
                meta={(
                  <>
                    {entry.email} · {entry.phone}<br />
                    {entry.street}, {entry.postalCode} {entry.city}, {entry.countryCode}
                    {entry.duplicateOf && <><br />Dublette von {entry.duplicateOf.name} ({entry.duplicateOf.email})</>}
                  </>
                )}
                onOpen={() => undefined}
                actions={[
                  { key: 'mail', label: 'E-Mail schreiben', icon: <MailOutlined />, onClick: () => { window.location.href = `mailto:${entry.email}`; } },
                  { key: 'call', label: 'Anrufen', icon: <PhoneOutlined />, onClick: () => { window.location.href = `tel:${entry.phone}`; } },
                  ...(action ? [ { ...action, divider: true } ] : []),
                ]}
              />
            );
          })}
        </List>
        {!loading && entries.length === 0 && (
          <EmptyState><h3>Keine Teilnahmen</h3><span>Für diesen Filter gibt es keine Teilnahmen.</span></EmptyState>
        )}
      </div>
    );
  }

  const columns = [
    { title: 'Name', key: 'name', render: (_: any, e: Entry) => `${e.firstName} ${e.lastName}` },
    { title: 'Anschrift', key: 'address', render: (_: any, e: Entry) => <>{e.street}<br />{e.postalCode} {e.city}, {e.countryCode}</> },
    { title: 'Kontakt', key: 'contact', render: (_: any, e: Entry) => <><a href={`mailto:${e.email}`}>{e.email}</a><br />{e.phone}</> },
    { title: 'Teilnahme', key: 'createdAt', render: (_: any, e: Entry) => formatDate(e.createdAt) },
    { title: 'Bestätigt', key: 'confirmedAt', render: (_: any, e: Entry) => formatDate(e.confirmedAt) },
    { title: 'Status', key: 'status', render: (_: any, e: Entry) => statusTags(e) },
    {
      title: 'Dublette',
      key: 'duplicate',
      render: (_: any, e: Entry) => {
        const action = duplicateAction(e);
        if (!action) return null;
        return (
          <Space direction="vertical" size={2}>
            <span style={{ fontSize: 12, color: '#8c8c8c' }}>von {e.duplicateOf!.name}</span>
            <Button size="small" icon={action.icon} onClick={action.onClick} data-testid={`entry-${action.key}-${e.id}`}>{action.label}</Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      {toolbar}
      <Table rowKey="id" columns={columns} dataSource={entries} loading={loading} pagination={{ pageSize: 50 }} />
    </div>
  );
};

export default GiveawayEntriesTab;
